// ─────────────────────────────────────────────────────────────────────────────
// AIR DUCT TX — the app, as a CLICKABLE prototype on the board.
//
// Erez, 2026-07-26: like the Boiling Offers single-tenant app, but you can click
// between the screens. Front-end only — no backend, no data layer, nothing real
// behind it. Its whole job is to make the scope tangible: every screen here is
// one of the use cases they described on 23 July.
//
// Visual language is deliberately lifted from BoilingOffersBoardPage so the two
// prototypes read as the same product family: navy rail, light app body, an
// always-on agent column on the right.
//
// ⚠️ EVERY NUMBER ON THESE SCREENS IS ILLUSTRATIVE. We have no revenue, job
// volume, ad spend or headcount from them beyond "~30 on commission, ~25 on
// payroll". The topbar says PROTOTYPE · SAMPLE DATA on every screen so nobody
// can mistake a mock for a finding — that matters more here than usual, because
// the whole engagement is about numbers they cannot currently trust.
//
// React Flow traps: this lives inside a board node, so it needs pointerEvents
// auto plus nowheel/nodrag/nopan or clicking a nav item pans the canvas.
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from "react";
import type React from "react";

// Notion palette (Erez, 2026-07-28). The biggest change is the RAIL: it was a
// dark navy sidebar, and Notion's is a light warm gray with dark text. That one
// swap is most of what makes the app read as Notion rather than as a generic
// SaaS console, so the rail's hardcoded light-on-dark colours below flipped too.
const A = {
  appBg: "#FFFFFF", panel: "#FFFFFF", soft: "#F7F6F3", border: "#E9E9E7",
  ink: "#37352F", ink2: "#787774", ink3: "#9B9A97", navy: "#F7F6F3",
  petrol: "#0F7B6C", green: "#0F7B6C", amber: "#CB912F", red: "#E03E3E",
  blue: "#0B6E99", violet: "#6940A5", lime: "#0B6E99",
};
const W = 1560, H = 880;

// TWO PERSONAS, and they barely share a screen. The owner CONSUMES — his loop
// is seeing, and he signs off on definitions. Operations RUNS it — the
// commission cycle, dispatch, and the three configure screens that answer the
// guardrail question. Per-employee-type permissions were promised to them in
// the room (Matan), so the app models roles, not people.
type Who = "owner" | "ops";
type Nav = { k?: string; label?: string; icon?: string; group?: string; tag?: string; who?: Who[] };
const BOTH: Who[] = ["owner", "ops"];
const NAV: Nav[] = [
  { group: "SEE", who: BOTH },
  { k: "home", icon: "🏠", label: "Home", who: BOTH },
  { k: "ask", icon: "✦", label: "Ask", who: BOTH },
  { k: "reporting", icon: "📈", label: "Reporting", who: BOTH },
  { k: "calls", icon: "🎧", label: "Call floor", tag: "?", who: BOTH },
  { group: "RUN", who: ["ops"] },
  { k: "dispatch", icon: "🚚", label: "Dispatch", who: ["ops"] },
  { k: "commissions", icon: "💵", label: "Commissions", who: ["ops"] },
  { k: "jobs", icon: "📋", label: "Jobs", who: ["ops"] },
  { group: "CONFIGURE", who: BOTH },
  // the one screen they SHARE — and the hinge of the whole product
  { k: "definitions", icon: "📐", label: "Definitions", who: BOTH },
  { k: "usecases", icon: "🧩", label: "Use cases", who: ["ops"] },
  { k: "guardrails", icon: "🛡️", label: "Guardrails", who: ["ops"] },
  { k: "connections", icon: "🔌", label: "Connections", who: ["ops"] },
];
const PERSONA: Record<Who, { label: string; sub: string; initial: string; home: string }> = {
  owner: { label: "Owner", sub: "sees everything, approves definitions", initial: "D", home: "home" },
  ops: { label: "Operations", sub: "runs it, and sets the limits", initial: "J", home: "commissions" },
};

// —— atoms ——
const Tile = ({ label, value, tone, note }: any) => (
  <div style={{ flex: 1, background: A.panel, border: `1px solid ${A.border}`, borderRadius: 4, padding: "13px 15px", minWidth: 0 }}>
    <div style={{ fontSize: 11.5, fontWeight: 600, color: A.ink3 }}>{label}</div>
    <div style={{ marginTop: 5, fontSize: 26, fontWeight: 700, letterSpacing: "-.02em", color: tone || A.ink }}>{value}</div>
    {note && <div style={{ marginTop: 2, fontSize: 11, fontWeight: 600, color: tone || A.ink3 }}>{note}</div>}
  </div>
);
const Panel = ({ children, style }: any) => (
  <div style={{ background: A.panel, border: `1px solid ${A.border}`, borderRadius: 4, ...style }}>{children}</div>
);
const Btn = ({ label, primary, color }: any) => {
  const c = color || A.petrol;
  return <span style={{ display: "inline-flex", alignItems: "center", fontSize: 12.5, fontWeight: 700, padding: "8px 14px", borderRadius: 4, whiteSpace: "nowrap", color: primary ? "#FFF" : c, background: primary ? c : "transparent", border: primary ? "none" : `1.5px solid ${c}` }}>{label}</span>;
};
const Pill = ({ t, bg, fg }: any) => (
  <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: ".07em", textTransform: "uppercase", padding: "3px 8px", borderRadius: 999, background: bg, color: fg, whiteSpace: "nowrap" }}>{t}</span>
);
const PROGRAM = { bg: "#E4EDF9", fg: A.blue };
const AGENT = { bg: "#EDE6F7", fg: A.violet };
const AUTO = { bg: "#E4F1E5", fg: A.green };
const COLLAB = { bg: "#FBF0DA", fg: A.amber };

const Row = ({ cells, head, tone }: { cells: React.ReactNode[]; head?: boolean; tone?: string }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1.8fr 1fr 1fr 1.2fr", gap: 10, padding: "9px 12px",
    background: tone, borderRadius: 4, alignItems: "center" }}>
    {cells.map((c, i) => (
      <div key={i} style={{ fontSize: head ? 10.5 : 12.5, fontWeight: head ? 800 : i === 0 ? 650 : 500,
        letterSpacing: head ? ".08em" : undefined, textTransform: head ? "uppercase" : undefined,
        color: head ? A.ink3 : i === 0 ? A.ink : A.ink2 }}>{c}</div>
    ))}
  </div>
);

// —— the agent rail: in this product the agent is the thing they asked for ——
function Rail({ msgs }: { msgs: { from: string; text: string; did?: string }[] }) {
  return (
    <div style={{ width: 310, background: A.navy, borderLeft: `1px solid ${A.border}`, flexShrink: 0, display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "13px 15px", borderBottom: `1px solid ${A.border}`, display: "flex", alignItems: "center", gap: 9 }}>
        <span style={{ width: 25, height: 25, borderRadius: 4, background: A.lime, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13 }}>✦</span>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: A.ink }}>Ask the system</div>
          <div style={{ fontSize: 10.5, color: A.ink3 }}>every answer cites its rows</div>
        </div>
      </div>
      <div style={{ flex: 1, padding: 13, display: "flex", flexDirection: "column", gap: 9, overflow: "hidden" }}>
        {msgs.map((b, i) => (
          <div key={i} style={{ display: "flex", justifyContent: b.from === "you" ? "flex-end" : "flex-start" }}>
            <div style={{ maxWidth: 246, fontSize: 11.5, lineHeight: 1.45, padding: "8px 11px", borderRadius: 4,
              background: b.from === "you" ? "#E3E2DF" : "#E7F3F8",
              color: A.ink,
              border: "none" }}>
              {b.from !== "you" && <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: ".06em", color: A.blue, marginBottom: 3 }}>SYSTEM</div>}
              {b.text}
              {b.did && <div style={{ marginTop: 6, fontSize: 10, fontWeight: 600, color: A.blue }}>↳ {b.did}</div>}
            </div>
          </div>
        ))}
      </div>
      <div style={{ padding: 11, borderTop: `1px solid ${A.border}`, display: "flex", gap: 8, alignItems: "center" }}>
        <div style={{ flex: 1, height: 32, background: A.panel, border: `1px solid ${A.border}`, borderRadius: 4, display: "flex", alignItems: "center", padding: "0 11px", fontSize: 11, color: A.ink3 }}>Ask anything…</div>
        <span style={{ width: 32, height: 32, borderRadius: 4, background: A.lime, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13 }}>🎤</span>
      </div>
    </div>
  );
}

// ── the screens ──────────────────────────────────────────────────────────────
function Screen({ k, go, who }: { k: string; go: (s: string) => void; who: Who }) {
  const owner = who === "owner";
  const click = (s: string) => ({ onClick: () => go(s), style: { cursor: "pointer" } as React.CSSProperties });

  if (k === "home") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 13, height: "100%" }}>
      <div style={{ display: "flex", gap: 11 }}>
        <Tile label="Jobs this week" value="—" note="once Workiz is connected" />
        <Tile label="Booked rate" value="—" tone={A.ink3} />
        <Tile label="Cost per booked job" value="—" tone={A.ink3} />
        <Tile label="Commission cycle" value="6 min" note="was two days" tone={A.green} />
      </div>
      <Panel style={{ padding: 16, flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: A.ink, marginBottom: 4 }}>Your six weakest points</div>
        <div style={{ fontSize: 12, color: A.ink2, marginBottom: 12 }}>The question you already asked once. Click one to see the numbers behind it.</div>
        {["Booked rate is falling on after-hours calls", "One campaign is carrying the cost per job", "Abandoned calls cluster on Monday mornings", "Jobs per crew per day is down on drive time", "Reschedules rose after the schedule change", "Three commission lines needed a human last cycle"].map((t, i) => (
          <div key={i} {...click(i === 5 ? "commissions" : "reporting")}
            style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 11, padding: "10px 12px", borderTop: i ? `1px solid ${A.border}` : "none" }}>
            <span style={{ width: 22, height: 22, borderRadius: 4, background: A.soft, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, color: A.ink3 }}>{i + 1}</span>
            <span style={{ fontSize: 12.5, color: A.ink, flex: 1 }}>{t}</span>
            <span style={{ fontSize: 11, color: A.blue, fontWeight: 700 }}>open →</span>
          </div>
        ))}
      </Panel>
    </div>
  );

  if (k === "ask") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 13, height: "100%" }}>
      <Panel style={{ padding: "13px 16px", display: "flex", alignItems: "center", gap: 10, border: `1.5px solid ${A.blue}` }}>
        <span style={{ color: A.blue, fontWeight: 700 }}>›</span>
        <span style={{ fontSize: 14, color: A.ink }}>Show me marketing return on ad spend this period, by channel and market</span>
      </Panel>
      <div style={{ display: "flex", gap: 13, flex: 1, minHeight: 0 }}>
        <Panel style={{ flex: 1, padding: 16 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: A.ink, marginBottom: 10 }}>The answer</div>
          <Row head cells={["Channel", "Spend", "Booked", "Return"]} />
          {[["Paid search", "—", "—", "—"], ["Lead aggregator A", "—", "—", "—"], ["Aggregator B", "—", "—", "—"], ["Branded / direct", "—", "—", "—"]].map((r, i) => (
            <Row key={i} cells={r} tone={i % 2 ? A.soft : undefined} />
          ))}
          <div style={{ marginTop: 12, fontSize: 11.5, color: A.ink2, lineHeight: 1.5, background: A.soft, borderRadius: 4, padding: "10px 12px" }}>
            Figures appear once the ad platforms and Workiz are connected. What matters here is that the answer states its basis and links to the rows it used — so it can be checked rather than believed.
          </div>
        </Panel>
        <Panel style={{ width: 330, padding: 16 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: A.ink, marginBottom: 8 }}>How it got there</div>
          {["Read: jobs, invoices, ad spend", "Basis: booked jobs by cohort", "Window: this period, matched", "Excluded: 0 rows"].map((t) => (
            <div key={t} style={{ fontSize: 11.5, color: A.ink2, padding: "7px 0", borderBottom: `1px solid ${A.border}` }}>{t}</div>
          ))}
          <div style={{ marginTop: 12 }}><Btn label="Open the rows" /></div>
        </Panel>
      </div>
    </div>
  );

  if (k === "reporting") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 13, height: "100%" }}>
      <Panel style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: A.ink }}>Basis</span>
        <Pill t="cohort" {...PROGRAM} /><Pill t="period" bg={A.soft} fg={A.ink3} />
        <span style={{ marginLeft: "auto", fontSize: 11.5, color: A.ink2 }}>Every chart on this page states which one it is using.</span>
      </Panel>
      <Panel style={{ padding: 16, flex: 1 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: A.ink }}>Booked jobs — by cohort</div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 10, height: 200, marginTop: 18 }}>
          {[46, 62, 38, 71, 55, 80, 64, 49].map((h, i) => (
            <div key={i} style={{ flex: 1, height: `${h}%`, background: i === 5 ? A.blue : "#CFE0F3", borderRadius: "6px 6px 0 0" }} />
          ))}
        </div>
        <div style={{ marginTop: 14, background: "#FDF3E4", border: `1px solid #EBD9B8`, borderRadius: 4, padding: "10px 13px", fontSize: 12, color: A.amber, lineHeight: 1.5 }}>
          <b>Blocked comparison.</b> You asked to put this beside “completed jobs”, which is measured by period. They answer different questions, so the system will not draw them as one chart — it will show them separately, or convert one, but it will not let them lie next to each other.
        </div>
      </Panel>
    </div>
  );

  if (k === "calls") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 13, height: "100%" }}>
      <div style={{ background: "#FDF3E4", border: "1px solid #EBD9B8", borderRadius: 4, padding: "11px 14px", fontSize: 12, color: A.amber, lineHeight: 1.5 }}>
        <b>This screen depends on an answer we do not have.</b> Handle time and abandoned rate are queue behaviours. If the queue lives inside the Workiz softphone, the tracking layer may never see an abandoned call — in which case this screen is scoped out honestly rather than promised.
      </div>
      <div style={{ display: "flex", gap: 11 }}>
        <Tile label="Avg handle time" value="—" /><Tile label="Abandoned rate" value="—" /><Tile label="Booked / answered" value="—" /><Tile label="After-hours share" value="—" />
      </div>
      <Panel style={{ padding: 16, flex: 1 }}>
        <Row head cells={["Who answered", "Calls", "Handle", "Booked"]} />
        {[["Front desk", "—", "—", "—"], ["Overflow — AI answering", "—", "—", "—"], ["After hours", "—", "—", "—"]].map((r, i) => <Row key={i} cells={r} tone={i % 2 ? A.soft : undefined} />)}
      </Panel>
    </div>
  );

  if (k === "dispatch") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 13, height: "100%" }}>
      <Panel style={{ padding: "11px 14px", background: "#FBEDE9", border: "1px solid #EBC9BE", display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ fontSize: 12.5, color: A.red, fontWeight: 600 }}>Crew 2 is running late on job 4. Two customers are affected.</span>
        <span style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          {owner
            ? <span style={{ fontSize: 11.5, color: A.ink3 }}>Dispatch handles this</span>
            : <><Btn label="Send the drafts" primary color={A.red} /><Btn label="Reassign job 6" /></>}
        </span>
      </Panel>
      <Panel style={{ padding: 16, flex: 1 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: A.ink, marginBottom: 12 }}>Today · capacity, not a calendar</div>
        {[["Crew 1 — north", 5, 5], ["Crew 2 — central", 7, 5], ["Crew 3 — south", 3, 5]].map(([n, booked, cap]: any, i) => (
          <div key={i} style={{ padding: "11px 0", borderTop: i ? `1px solid ${A.border}` : "none" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 12.5, fontWeight: 650, color: A.ink, width: 150 }}>{n}</span>
              <span style={{ fontSize: 11.5, color: booked > cap ? A.red : A.ink2 }}>{booked} booked · capacity {cap}</span>
              {booked > cap && <Pill t="overbooked" bg="#FBEDE9" fg={A.red} />}
            </div>
            <div style={{ display: "flex", gap: 5 }}>
              {Array.from({ length: Math.max(booked, cap) }).map((_, j) => (
                <div key={j} style={{ flex: 1, height: 14, borderRadius: 4, background: j >= cap ? A.red : j < booked ? A.blue : "#E3E7EC" }} />
              ))}
            </div>
          </div>
        ))}
        <div style={{ marginTop: 14, fontSize: 11.5, color: A.ink2, background: A.soft, borderRadius: 4, padding: "10px 12px", lineHeight: 1.5 }}>
          A capacity model needs crew count, job durations by service and drive time — none of which exists as data today. That is why this screen is later in the build, not first.
        </div>
      </Panel>
    </div>
  );

  if (k === "commissions") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 13, height: "100%" }}>
      <div style={{ display: "flex", gap: 11 }}>
        <Tile label="People in this cycle" value="~30" note="plus ~25 on payroll" />
        <Tile label="Computed" value="27" tone={A.green} />
        <Tile label="Needs a human" value="3" tone={A.amber} note="the last 10%" />
        <Tile label="Time" value="6 min" tone={A.green} note="was two days" />
      </div>
      <Panel style={{ padding: 16, flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: A.ink }}>Cycle · 1–15</div>
          <span style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
            {owner
              ? <span style={{ fontSize: 11.5, color: A.ink3 }}>Operations approves this run — you see the result</span>
              : <><Btn label="Approve 27" primary color={A.green} /><Btn label="Review 3" color={A.amber} /><Btn label="Export" /></>}
          </span>
        </div>
        <Row head cells={["Technician", "Jobs", "Plan", "Commission"]} />
        {[["Technician A", "38", "Plan A", "computed"], ["Technician B", "41", "Plan A", "computed"]].map((r, i) => <Row key={i} cells={r} tone={i % 2 ? A.soft : undefined} />)}
        <Row cells={["Technician C", "29", "Plan B — insulation", <span key="x" style={{ color: A.amber, fontWeight: 700 }}>needs you</span>]} tone="#FDF7EC" />
        <div style={{ marginTop: 12, fontSize: 11.5, color: A.ink2, background: A.soft, borderRadius: 4, padding: "10px 12px", lineHeight: 1.5 }}>
          Two jobs were reclassified after invoicing, so the rate is ambiguous. Everything else is computed. <b>This is a program, not an agent</b> — the rules are known, so it can only ever do this one thing.
        </div>
      </Panel>
    </div>
  );

  if (k === "jobs") return (
    <Panel style={{ padding: 16, height: "100%" }}>
      <div style={{ fontSize: 13.5, fontWeight: 700, color: A.ink, marginBottom: 10 }}>Jobs — one row per job, traceable to Workiz</div>
      <Row head cells={["Job", "Service", "Crew", "State"]} />
      {[["#—", "Air duct cleaning", "Crew 1", "completed"], ["#—", "Dryer vent", "Crew 3", "scheduled"], ["#—", "Insulation", "Crew 2", "en route"], ["#—", "Air duct cleaning", "Crew 1", "invoiced"]].map((r, i) => <Row key={i} cells={r} tone={i % 2 ? A.soft : undefined} />)}
      <div style={{ marginTop: 14, fontSize: 11.5, color: A.ink2, background: A.soft, borderRadius: 4, padding: "10px 12px", lineHeight: 1.5 }}>
        Every number anywhere in this app resolves back to rows like these. That is what makes an answer checkable instead of something to take on trust.
      </div>
    </Panel>
  );

  if (k === "definitions") return (
    <Panel style={{ padding: 16, height: "100%" }}>
      <div style={{ fontSize: 13.5, fontWeight: 700, color: A.ink }}>Definitions — the fix for the reporting you do not trust</div>
      <div style={{ fontSize: 12, color: A.ink2, margin: "5px 0 12px" }}>Agreed once with you, then applied everywhere. Nothing else in the app can disagree with this page.</div>
      <Row head cells={["Metric", "Basis", "Source", "Agreed"]} />
      {[["Lead", "event", "CallRail + forms", "—"], ["Booked job", "cohort", "Workiz", "—"], ["Completed job", "period", "Workiz", "—"], ["Cancellation", "event", "Workiz", "—"], ["Revenue", "period", "invoices", "—"], ["Commission event", "cohort", "jobs + plan", "—"]].map((r, i) => (
        <Row key={i} cells={r} tone={i % 2 ? A.soft : undefined} />
      ))}
      <div style={{ marginTop: 14, background: "#E8F1FB", border: `1px solid #C9DDF2`, borderRadius: 4, padding: "11px 13px", fontSize: 12, color: A.blue, lineHeight: 1.5 }}>
        <b>The one screen you both open.</b> If a job means one thing to the owner and another to operations, that is the root of the false positives — not the charting tool. {owner ? "Nothing else in the app can disagree with what you sign here." : "Operations proposes; the owner signs."}
      </div>
      <div style={{ marginTop: 12 }}>{owner ? <Btn label="Approve these definitions" primary color={A.blue} /> : <Btn label="Propose a change" />}</div>
    </Panel>
  );

  if (k === "usecases") return (
    <Panel style={{ padding: 16, height: "100%", overflow: "hidden" }}>
      <div style={{ fontSize: 13.5, fontWeight: 700, color: A.ink, marginBottom: 10 }}>Use cases — and how each one is delivered</div>
      {[
        ["Connect + keep it true", "reads every system, repairs a renamed column", PROGRAM, AUTO, "program"],
        ["Definitions", "one agreed meaning per number", PROGRAM, COLLAB, "collaborative"],
        ["Ask in plain language", "answers, with the rows it used", AGENT, COLLAB, "collaborative"],
        ["Reporting you trust", "cohort and period never drawn as equals", PROGRAM, AUTO, "automated"],
        ["The commission run", "computes, flags the last 10%, you approve", PROGRAM, COLLAB, "collaborative"],
        ["Real availability", "no overbooking, after-hours honest", PROGRAM, COLLAB, "collaborative"],
        ["Running-late handoff", "drafts the message, a person sends it", AGENT, COLLAB, "collaborative"],
        ["Call visibility", "only as far as the tracking layer reaches", PROGRAM, COLLAB, "conditional"],
        ["The daily brief", "what moved, what went quiet", AGENT, AUTO, "automated"],
      ].map(([n, s, kind, mode]: any, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderTop: i ? `1px solid ${A.border}` : "none" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12.5, fontWeight: 650, color: A.ink }}>{n}</div>
            <div style={{ fontSize: 11, color: A.ink2, marginTop: 1 }}>{s}</div>
          </div>
          <Pill t={kind === PROGRAM ? "program" : "agent"} {...kind} />
          <Pill t={mode === AUTO ? "automated" : "collaborative"} {...mode} />
        </div>
      ))}
    </Panel>
  );

  if (k === "guardrails") return (
    <Panel style={{ padding: 16, height: "100%" }}>
      <div style={{ fontSize: 13.5, fontWeight: 700, color: A.ink }}>Guardrails — your thresholds, not ours</div>
      <div style={{ fontSize: 12, color: A.ink2, margin: "5px 0 12px" }}>Set per process. Where you say never, a person approves.</div>
      <Row head cells={["Process", "Autonomy", "Approver", "Writes to"]} />
      {[["Daily brief", "automatic", "—", "nothing"], ["Connector sync + repair", "automatic + watcher", "—", "your warehouse"], ["Reschedule a job", "proposes", "dispatch", "Workiz"], ["Commission run", "proposes", "operations", "nothing until approved"], ["Message a customer", "proposes", "dispatch", "nothing until approved"]].map((r, i) => (
        <Row key={i} cells={r} tone={i % 2 ? A.soft : undefined} />
      ))}
      <Row cells={["Anything in the books", <span key="n" style={{ color: A.red, fontWeight: 700 }}>never</span>, "—", "blocked"]} tone="#FBEDE9" />
      <div style={{ marginTop: 14, fontSize: 11.5, color: A.ink2, background: A.soft, borderRadius: 4, padding: "10px 12px", lineHeight: 1.5 }}>
        Per-employee permissions sit alongside this, so someone gets access to what they need and nothing beyond it — with training material that stays with you for people who join later.
      </div>
    </Panel>
  );

  if (k === "connections") return (
    <Panel style={{ padding: 16, height: "100%" }}>
      <div style={{ fontSize: 13.5, fontWeight: 700, color: A.ink, marginBottom: 10 }}>Connections — and whether they are still true</div>
      <Row head cells={["Source", "Last sync", "Rows", "State"]} />
      {[["Workiz — jobs", "4 min ago", "—", "healthy"], ["Workiz — invoices", "2 h ago", "0", "column renamed"], ["CallRail", "6 min ago", "—", "healthy"], ["Ad platforms", "—", "—", "not connected"], ["Payroll", "—", "—", "not connected"]].map((r, i) => (
        <Row key={i} cells={r} tone={r[3] === "column renamed" ? "#FDF7EC" : r[3] === "not connected" ? "#F6F7F8" : i % 2 ? A.soft : undefined} />
      ))}
      <div style={{ marginTop: 14, background: "#E4F1E5", border: "1px solid #C6E0C8", borderRadius: 4, padding: "11px 13px", fontSize: 12, color: A.green, lineHeight: 1.5 }}>
        <b>Repaired automatically.</b> “invoice_total” became “total_amount”. The mapping was updated and the affected rows were re-synced. You are being told, rather than finding out from a dashboard that quietly went wrong days ago.
      </div>
    </Panel>
  );

  return <Panel style={{ padding: 20, height: "100%" }}>Screen: {k}</Panel>;
}

const RAIL: Record<string, { from: string; text: string; did?: string }[]> = {
  home: [{ from: "system", text: "Six things moved against you this period. The biggest is after-hours booking rate." }],
  ask: [{ from: "you", text: "marketing ROAS this period, by channel and market" }, { from: "system", text: "Sliced by channel, campaign and market. Basis is booked jobs by cohort.", did: "cited 4 sources" }],
  reporting: [{ from: "system", text: "I stopped that comparison — those two metrics use different bases.", did: "offered a like-for-like version" }],
  calls: [{ from: "system", text: "I can only report what the tracking layer sees. Abandoned calls may sit inside the closed phone system." }],
  dispatch: [{ from: "system", text: "Crew 2 is late. I have drafted both customer messages — I will not send them without you." }],
  commissions: [{ from: "system", text: "27 of 30 computed cleanly. Three need a decision, all for the same reason." }],
  jobs: [{ from: "system", text: "Every figure in this app resolves to rows like these." }],
  definitions: [{ from: "system", text: "Two metrics here are still unagreed. Until they are, I will not put them on one chart." }],
  usecases: [{ from: "system", text: "Predictable work is a program. I am only used where judgement is genuinely needed." }],
  guardrails: [{ from: "system", text: "You set these. Where you say never, I stop and ask." }],
  connections: [{ from: "system", text: "A column moved upstream. I repaired the mapping and re-synced.", did: "re-synced the affected rows" }],
};

// Where the two personas hear something genuinely different from the system.
const RAIL_OWNER: Record<string, { from: string; text: string; did?: string }[]> = {
  commissions: [{ from: "system", text: "Operations is running this cycle. You will see the result and the three exceptions once it is approved." }],
  definitions: [{ from: "system", text: "Two definitions are waiting on you. Until you sign them I will not put those two metrics on one chart." }],
  home: [{ from: "system", text: "Six things moved against you this period. The biggest is after-hours booking rate." }],
};

/** Optional CONTROLLED props. With none, the app owns its own state and is the
 *  clickable prototype on the board. With them, the caller drives which screen
 *  and persona are showing — which is how the Remotion demo film walks it, so
 *  the video is the real component rather than screenshots of it
 *  (skill://remotion: the video is CODE). */
export function AirDuctApp({ screen, persona }: { screen?: string; persona?: Who } = {}) {
  const [whoState, setWho] = useState<Who>("owner");
  const [sState, setS] = useState("home");
  const who = persona ?? whoState;
  const s = screen ?? sState;
  const visible = NAV.filter((n) => (n.who ?? BOTH).includes(who));
  const label = NAV.find((n) => n.k === s)?.label ?? "";

  // switching persona: if the screen you were on is not theirs, land on their home
  const switchTo = (w: Who) => {
    setWho(w);
    const allowed = NAV.some((n) => n.k === s && (n.who ?? BOTH).includes(w));
    if (!allowed) setS(PERSONA[w].home);
  };
  return (
    <div
      className="nowheel nodrag nopan"
      data-zoom
      onPointerDown={(e) => e.stopPropagation()}
      style={{ pointerEvents: "auto", width: W, height: H, background: A.panel, borderRadius: 4,
        overflow: "hidden", boxShadow: "0 10px 30px rgba(16,24,40,.10)", display: "flex",
        fontFamily: "Inter, system-ui, -apple-system, sans-serif" }}
    >
      {/* rail */}
      <div style={{ width: 208, background: A.navy, borderRight: `1px solid ${A.border}`, padding: "15px 11px", display: "flex", flexDirection: "column", gap: 2, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "2px 8px 13px" }}>
          <span style={{ width: 21, height: 21, borderRadius: 4, background: A.lime, flexShrink: 0 }} />
          <span style={{ fontSize: 14, fontWeight: 600, color: A.ink }}>Air Duct TX</span>
        </div>
        {visible.map((n, i) => n.group ? (
          <div key={`g${i}`} style={{ fontSize: 9, fontWeight: 800, letterSpacing: ".14em", color: A.ink3, padding: "11px 10px 3px" }}>{n.group}</div>
        ) : (
          <div key={n.k} onClick={() => setS(n.k!)}
            style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 10, padding: "6px 10px", borderRadius: 4,
              background: n.k === s ? "rgba(55,53,47,.08)" : "transparent",
              color: n.k === s ? A.ink : A.ink2, fontSize: 12.5, fontWeight: n.k === s ? 600 : 500 }}>
            <span style={{ fontSize: 12, opacity: n.k === s ? 1 : .75 }}>{n.icon}</span>{n.label}
            {n.tag && <span style={{ marginLeft: "auto", fontSize: 9, fontWeight: 800, color: A.amber, background: "rgba(176,122,30,.18)", borderRadius: 999, padding: "1px 6px" }}>{n.tag}</span>}
            {n.k === s && !n.tag && <span style={{ marginLeft: "auto", width: 5, height: 5, borderRadius: 999, background: A.lime }} />}
          </div>
        ))}
        <div style={{ marginTop: "auto", display: "flex", alignItems: "center", gap: 9, padding: 8, borderTop: `1px solid ${A.border}` }}>
          <span style={{ width: 25, height: 25, borderRadius: 999, background: "#E3E2DF", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 10.5, fontWeight: 600, color: A.ink }}>{PERSONA[who].initial}</span>
          <span style={{ minWidth: 0 }}>
            <span style={{ display: "block", fontSize: 11.5, color: A.ink, fontWeight: 600 }}>{PERSONA[who].label}</span>
            <span style={{ display: "block", fontSize: 9.5, color: A.ink3, lineHeight: 1.3 }}>{PERSONA[who].sub}</span>
          </span>
        </div>
      </div>

      {/* body */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", background: A.appBg, minWidth: 0 }}>
        <div style={{ height: 50, background: A.panel, borderBottom: `1px solid ${A.border}`, display: "flex", alignItems: "center", gap: 12, padding: "0 18px", flexShrink: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: A.ink }}>{label}</div>
          <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: ".08em", color: A.amber, background: "#FBF0DA", borderRadius: 999, padding: "3px 9px" }}>PROTOTYPE · SAMPLE DATA</span>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 11, color: A.ink3 }}>viewing as</span>
            <div style={{ display: "flex", gap: 3, background: A.soft, border: `1px solid ${A.border}`, borderRadius: 999, padding: 3 }}>
              {(["owner", "ops"] as Who[]).map((w) => (
                <span key={w} onClick={() => switchTo(w)}
                  style={{ cursor: "pointer", fontSize: 11.5, fontWeight: 700, padding: "5px 13px", borderRadius: 999,
                    background: who === w ? A.ink : "transparent", color: who === w ? "#FFF" : A.ink2 }}>
                  {PERSONA[w].label}
                </span>
              ))}
            </div>
          </div>
        </div>
        <div style={{ flex: 1, padding: 16, overflow: "hidden", minHeight: 0 }}>
          <Screen k={s} go={setS} who={who} />
        </div>
      </div>

      <Rail msgs={(who === "owner" ? RAIL_OWNER[s] : undefined) ?? RAIL[s] ?? RAIL.home} />
    </div>
  );
}
